import Dexie, { type EntityTable } from 'dexie'
import type {
  Booking, DocumentAttachment, Expense, ExpenseCategory, ExpenseShare, Item,
  LinkRow, Note, Outbox, Place, TravelDoc, Trip, User,
} from './types'

/**
 * The local mirror. Every screen reads from here and only from here, online or
 * not — the network's only job is to keep this fresh in the background. That one
 * decision is what removes every spinner from the app.
 *
 * guest_invites is deliberately absent: a cached copy on a phone is exactly what
 * would keep a revoked link working.
 */
class TravelDB extends Dexie {
  users!: EntityTable<User, 'id'>
  trips!: EntityTable<Trip, 'id'>
  places!: EntityTable<Place, 'id'>
  items!: EntityTable<Item, 'id'>
  bookings!: EntityTable<Booking, 'id'>
  expenses!: EntityTable<Expense, 'id'>
  expense_shares!: EntityTable<ExpenseShare, 'expense_id'>
  expense_categories!: EntityTable<ExpenseCategory, 'id'>
  notes!: EntityTable<Note, 'id'>
  links!: EntityTable<LinkRow, 'id'>
  documents!: EntityTable<TravelDoc, 'id'>
  document_attachments!: EntityTable<DocumentAttachment, 'document_id'>
  outbox!: EntityTable<Outbox, 'opId'>
  meta!: EntityTable<{ key: string; value: unknown }, 'key'>

  constructor() {
    super('travel')
    // v1 shipped before activities were modelled and before documents existed.
    // Dexie only applies a store change when the version number goes up: declare
    // new stores at the same version and an existing browser keeps the old
    // schema, so db.documents is simply missing there. Anyone who opened the app
    // before today has a v1 database, so this needs its own version.
    this.version(1).stores({
      users: 'id, email',
      trips: 'id, updated_at',
      places: 'id, trip_id, sort_order',
      items: 'id, trip_id, place_id, starts_at',
      bookings: 'id, trip_id, starts_at',
      expenses: 'id, trip_id, spent_at, paid_by, category',
      expense_shares: '[expense_id+user_id], expense_id, user_id',
      notes: 'id, trip_id, place_id',
      links: 'id, trip_id, place_id',
      outbox: 'opId, queuedAt',
      meta: 'key',
    })

    this.version(2).stores({
      users: 'id, email',
      trips: 'id, updated_at',
      places: 'id, trip_id, sort_order',
      items: 'id, trip_id, place_id, starts_at, day_date, status',
      bookings: 'id, trip_id, starts_at',
      expenses: 'id, trip_id, spent_at, paid_by, category',
      expense_shares: '[expense_id+user_id], expense_id, user_id',
      notes: 'id, trip_id, place_id',
      links: 'id, trip_id, place_id, item_id',
      documents: 'id, trip_id, owner_user_id, kind, expires_on',
      document_attachments: '[document_id+entity_type+entity_id], document_id, entity_id',
      outbox: 'opId, queuedAt',
      meta: 'key',
    }).upgrade(async (tx) => {
      // The pull cursor is reset so the next sync refetches every row with the
      // columns v1 never stored. Cheaper and safer than backfilling by hand.
      await tx.table('meta').put({ key: 'syncCursor', value: 0 })
    })

    // v3 adds the party's own spend categories. Same rule as v2: a new store
    // declared at an existing version is ignored by a browser that already
    // opened the database, and db.expense_categories would be undefined there.
    this.version(3).stores({
      users: 'id, email',
      trips: 'id, updated_at',
      places: 'id, trip_id, sort_order',
      items: 'id, trip_id, place_id, starts_at, day_date, status',
      bookings: 'id, trip_id, starts_at',
      expenses: 'id, trip_id, spent_at, paid_by, category',
      expense_shares: '[expense_id+user_id], expense_id, user_id',
      expense_categories: 'id, trip_id, sort_order',
      notes: 'id, trip_id, place_id',
      links: 'id, trip_id, place_id, item_id',
      documents: 'id, trip_id, owner_user_id, kind, expires_on',
      document_attachments: '[document_id+entity_type+entity_id], document_id, entity_id',
      outbox: 'opId, queuedAt',
      meta: 'key',
    }).upgrade(async (tx) => {
      await tx.table('meta').put({ key: 'syncCursor', value: 0 })
    })
  }
}

export const db = new TravelDB()

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key)
  return row ? (row.value as T) : fallback
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value })
}

/** Rows the user has not deleted. Soft-deleted rows stay for sync bookkeeping. */
export const alive = <T extends { deleted_at: number | null }>(rows: T[]): T[] =>
  rows.filter((r) => r.deleted_at === null || r.deleted_at === undefined)
